import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Film, Clapperboard, Trophy, Frown, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { MovieSearch } from "./MovieSearch";
import { GuessTiles } from "./GuessTiles";
import { Countdown } from "./Countdown";
import { ShareCard } from "./ShareCard";
import { DialoguePlayer } from "./DialoguePlayer";
import { trackEvent } from "../lib/analytics";
import {
  getGame,
  saveGame,
  buildTiles,
  scoreForGuess,
} from "../lib/storage";
import { prettyDate } from "../lib/ist";

const DIFFICULTY = ["Very difficult", "Slightly easier", "Recognizable", "Famous scene", "Very recognizable"];

/**
 * Reusable game engine.
 * props:
 *  backendMode: "daily" | "dialogue"  (what to query on the server)
 *  storageMode: "daily" | "timemachine" | "dialogue"  (how to record locally)
 *  date: YYYY-MM-DD
 *  preview: boolean (admin preview - no persistence)
 *  showCountdown: boolean
 *  modeLabel: string (share card label)
 */
export const GameBoard = ({
  backendMode,
  storageMode,
  date,
  preview = false,
  showCountdown = true,
  modeLabel = "DAILY MOVIE",
}) => {
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // game state
  const [status, setStatus] = useState("playing"); // playing | won | lost
  const [guessesUsed, setGuessesUsed] = useState(0);
  const [clueIndex, setClueIndex] = useState(0); // highest revealed clue/clip index (0-based)
  const [reveal, setReveal] = useState(null);
  const [score, setScore] = useState(0);
  const [wrongShake, setWrongShake] = useState(false);
  const [history, setHistory] = useState([]); // [{ label, result: 'correct'|'incorrect' }]

  const restore = useCallback(() => {
    if (preview) {
      setHistory([]);
      return;
    }
    const g = getGame(storageMode, date);
    if (g) {
      setStatus(g.status);
      setGuessesUsed(g.guessesUsed);
      setClueIndex(g.clueIndex ?? g.guessesUsed);
      setReveal(g.reveal || null);
      setScore(g.score || 0);
      setHistory(g.history || []);
    } else {
      setStatus("playing");
      setGuessesUsed(0);
      setClueIndex(0);
      setReveal(null);
      setScore(0);
      setHistory([]);
    }
  }, [preview, storageMode, date]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get(`/challenge`, {
        params: { mode: backendMode, date },
        timeout: 30000,
      });
      setChallenge(data);
    } catch (e) {
      // Distinguish a temporary API/network failure from a genuinely absent challenge.
      setChallenge(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [backendMode, date]);

  useEffect(() => {
    load();
    restore();
    setSelected(null);
    if (!preview) {
      const g = getGame(storageMode, date);
      if (!g || g.status === "playing") trackEvent("game_started", { mode: backendMode, date });
    }
  }, [load, restore]);

  const persist = (partial) => {
    if (preview) return;
    const rec = {
      mode: storageMode,
      date,
      status: partial.status,
      guessesUsed: partial.guessesUsed,
      clueIndex: partial.clueIndex,
      score: partial.score,
      reveal: partial.reveal || null,
      history: partial.history || [],
      tiles: buildTiles(partial.status, partial.guessesUsed),
      completedAt:
        partial.status !== "playing" ? new Date().toISOString() : undefined,
    };
    saveGame(rec);
    if (partial.status !== "playing") {
      api
        .post(`/results`, {
          mode: backendMode,
          date,
          won: partial.status === "won",
          guesses: partial.guessesUsed,
          score: partial.score,
        })
        .catch(() => {});
    }
  };

  const submitGuess = async ({ movieId, label }) => {
    if (submitting) return;
    setSubmitting(true);
    const guessNumber = guessesUsed + 1;
    try {
      const { data } = await api.post(`/challenge/guess`, {
        mode: backendMode,
        date,
        movie_id: movieId,
        guess_number: guessNumber,
      });
      const newGuesses = guessNumber;
      const newHistory = [...history, { label, result: data.correct ? "correct" : "incorrect" }];
      setHistory(newHistory);
      trackEvent("guess_submitted", { mode: backendMode, guess_number: guessNumber, skipped: label === "SKIPPED" });
      if (data.correct) {
        const sc = scoreForGuess(guessNumber);
        trackEvent("correct_guess", { mode: backendMode, guess_number: guessNumber, score: sc });
        trackEvent("game_completed", { mode: backendMode, result: "won", score: sc, guesses: guessNumber });
        setStatus("won");
        setGuessesUsed(newGuesses);
        setReveal(data.reveal);
        setScore(sc);
        persist({ status: "won", guessesUsed: newGuesses, clueIndex, score: sc, reveal: data.reveal, history: newHistory });
      } else if (data.game_over) {
        trackEvent("incorrect_guess", { mode: backendMode, guess_number: guessNumber });
        trackEvent("game_completed", { mode: backendMode, result: "lost", score: 0, guesses: guessNumber });
        setStatus("lost");
        setGuessesUsed(newGuesses);
        setReveal(data.reveal);
        setScore(0);
        persist({ status: "lost", guessesUsed: newGuesses, clueIndex: 4, score: 0, reveal: data.reveal, history: newHistory });
        toast.error("Out of guesses");
      } else {
        trackEvent("incorrect_guess", { mode: backendMode, guess_number: guessNumber });
        setGuessesUsed(newGuesses);
        const nextClue = Math.min(newGuesses, 4);
        setClueIndex(nextClue);
        persist({ status: "playing", guessesUsed: newGuesses, clueIndex: nextClue, score: 0, history: newHistory });
        setWrongShake(true);
        setTimeout(() => setWrongShake(false), 500);
        toast(label === "SKIPPED" ? "⏭️ Skipped" : "❌ Wrong guess", {
          description: `${5 - newGuesses} guess${5 - newGuesses === 1 ? "" : "es"} remaining`,
        });
      }
      setSelected(null);
    } catch (e) {
      toast.error("Could not submit guess");
    } finally {
      setSubmitting(false);
    }
  };

  const doGuess = () => {
    if (!selected) return;
    submitGuess({ movieId: selected.id, label: `${selected.title}` });
  };

  const doSkip = () => submitGuess({ movieId: "__skip__", label: "SKIPPED" });

  const resetPreview = () => {
    setStatus("playing");
    setGuessesUsed(0);
    setClueIndex(0);
    setReveal(null);
    setScore(0);
    setSelected(null);
    setHistory([]);
  };

  if (loading) {
    return (
      <div className="ac-card p-10 text-center text-zinc-400" data-testid="game-loading">
        <Clapperboard className="w-8 h-8 mx-auto mb-3 ac-gold-text animate-pulse" />
        Loading challenge…
      </div>
    );
  }

  if (error) {
    return (
      <div className="ac-card p-10 text-center" data-testid="challenge-error">
        <Frown className="w-10 h-10 mx-auto mb-3 text-zinc-600" />
        <div className="font-display text-xl text-amber-100">Couldn't load the challenge</div>
        <div className="text-sm text-zinc-500 mt-2">
          This is a temporary problem on our side — not a missing movie. Please try again in a moment.
        </div>
        <button
          onClick={load}
          data-testid="challenge-retry-btn"
          className="mt-5 inline-flex items-center gap-2 px-5 py-2 rounded-full border border-amber-400/40 ac-gold-text text-sm font-semibold hover:bg-amber-400/10 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!challenge?.available) {
    return (
      <div className="ac-card p-10 text-center" data-testid="no-challenge">
        <Film className="w-10 h-10 mx-auto mb-3 text-zinc-600" />
        <div className="font-display text-xl text-amber-100">No challenge scheduled</div>
        <div className="text-sm text-zinc-500 mt-2">
          There's no movie assigned to {prettyDate(date)} yet.
        </div>
      </div>
    );
  }

  const remaining = 5 - guessesUsed;
  const tiles = buildTiles(status, guessesUsed);
  const guessLabel =
    remaining === 5 ? "5 GUESSES" : `${remaining} GUESS${remaining === 1 ? "" : "ES"} REMAINING`;

  // ---------------- RESULT SCREENS ----------------
  if (status === "won") {
    return (
      <div className="ac-fade-up space-y-6" data-testid="success-screen">
        <div className="ac-card p-6 sm:p-8 text-center marquee-border relative overflow-hidden">
          <Sparkles className="w-8 h-8 mx-auto ac-gold-text mb-2" />
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-wider ac-gold-text">
            🎬 ABSOLUTE CINEMA!
          </h2>
          <p className="text-zinc-400 mt-1">You guessed {challenge.is_today ? "today's" : "the"} movie!</p>
          <div className="mt-5 flex justify-center">
            <GuessTiles tiles={tiles} size="lg" />
          </div>
          <div className="mt-6 space-y-1">
            <div className="font-display text-2xl sm:text-3xl font-bold text-zinc-50" data-testid="reveal-title">
              {reveal?.title}
            </div>
            <div className="text-amber-300 font-mono-ac">{reveal?.year}</div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-6">
            <Stat label="Guesses" value={`${guessesUsed} / 5`} />
            <Stat label="Score" value={score} testid="final-score" />
            <Stat label="Date" value={prettyDate(date).split(",")[0]} />
          </div>
        </div>
        <CompletedAudioList date={date} backendMode={backendMode} />
        <ShareCard tiles={tiles} resultLabel={`${guessesUsed} / 5`} won mode={modeLabel} />
        {showCountdown && (
          <div className="ac-card p-5">
            <Countdown onReset={() => window.location.reload()} />
          </div>
        )}
        {preview && <PreviewReset onReset={resetPreview} />}
      </div>
    );
  }

  if (status === "lost") {
    return (
      <div className="ac-fade-up space-y-6" data-testid="failure-screen">
        <div className="ac-card p-6 sm:p-8 text-center">
          <Frown className="w-8 h-8 mx-auto text-rose-400 mb-2" />
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-wider text-rose-300">
            🎞️ OUT OF GUESSES
          </h2>
          <div className="mt-5 flex justify-center">
            <GuessTiles tiles={tiles} size="lg" />
          </div>
          <div className="font-mono-ac text-xl font-bold text-zinc-200 mt-3">0 / 5</div>
          <div className="mt-6 pt-5 border-t border-amber-500/15">
            <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mb-2">
              Today's movie was
            </div>
            <div className="font-display text-2xl sm:text-3xl font-bold text-amber-100" data-testid="reveal-title">
              {reveal?.title}
            </div>
            <div className="text-amber-300 font-mono-ac mt-1">{reveal?.year}</div>
            <div className="text-sm text-zinc-400 mt-3 space-y-1">
              <div><span className="text-zinc-500">Director:</span> {reveal?.director}</div>
              <div><span className="text-zinc-500">Genre:</span> {reveal?.genre} · {reveal?.language}</div>
              {reveal?.description && <p className="mt-2 italic">{reveal.description}</p>}
            </div>
          </div>
        </div>
        <CompletedAudioList date={date} backendMode={backendMode} />
        <ShareCard tiles={tiles} resultLabel="0 / 5" won={false} mode={modeLabel} />
        {showCountdown && (
          <div className="ac-card p-5">
            <Countdown onReset={() => window.location.reload()} />
          </div>
        )}
        {preview && <PreviewReset onReset={resetPreview} />}
      </div>
    );
  }

  // ---------------- PLAYING ----------------
  const clues = challenge.clues || [];
  const hasClues = clues.length === 5;
  const visibleClues = hasClues ? clues.slice(0, clueIndex + 1) : [];

  return (
    <div className="space-y-5" data-testid="game-board">
      {/* guesses remaining banner */}
      <div className="flex items-center justify-between ac-card px-5 py-3">
        <div className="flex items-center gap-3">
          <GuessTiles tiles={tiles} size="sm" />
        </div>
        <div
          data-testid="guesses-remaining"
          className={`font-display text-sm sm:text-lg font-bold tracking-wider ${
            remaining <= 1 ? "text-rose-400" : "text-amber-200"
          }`}
        >
          {guessLabel}
        </div>
      </div>

      {/* Audio clips (progressive) + optional admin-authored text clues */}
      <div className="space-y-3">
        <p className="text-center text-zinc-400 text-sm">
          🎧 Listen to the clip{hasClues ? " & read the clue" : ""} — each wrong guess unlocks an easier one
        </p>
        {Array.from({ length: clueIndex + 1 }).map((_, i) => (
          <DialoguePlayer
            key={i}
            mode={backendMode}
            date={date}
            slot={i + 1}
            label={`Audio Clue ${i + 1}`}
            difficulty={DIFFICULTY[i]}
          />
        ))}
        {hasClues &&
          visibleClues.map((c, i) => (
            <div
              key={`clue-${i}`}
              data-testid={`clue-card-${i + 1}`}
              className={`ac-card p-4 sm:p-5 ac-fade-up ${i === clueIndex ? "marquee-border" : "opacity-80"}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold uppercase tracking-widest ac-gold-text">
                  Clue {i + 1}
                </span>
                {i === clueIndex && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300">
                    NEW
                  </span>
                )}
              </div>
              <p className="text-zinc-200 leading-relaxed">{c}</p>
            </div>
          ))}
        <p className="text-center text-amber-200 font-semibold text-sm mt-1">
          What movie is this from?
        </p>
      </div>

      {/* search + guess + skip */}
      <div className={`space-y-3 ${wrongShake ? "animate-[ac-fade-up_0.4s]" : ""}`}>
        <MovieSearch
          key={`search-${guessesUsed}`}
          onSelect={setSelected}
          onClear={() => setSelected(null)}
          disabled={submitting}
        />
        <div className="flex gap-3">
          <button
            data-testid="guess-submit-button"
            disabled={!selected || submitting}
            onClick={doGuess}
            className="btn-gold flex-1 py-4 rounded-xl text-lg font-black tracking-widest gold-glow"
          >
            {submitting ? "CHECKING…" : "GUESS"}
          </button>
          <button
            data-testid="skip-submit-button"
            disabled={submitting}
            onClick={doSkip}
            className="px-5 py-4 rounded-xl border border-amber-500/30 text-amber-200 font-bold tracking-wider hover:bg-amber-500/10 active:scale-95 transition-all disabled:opacity-40"
          >
            SKIP
          </button>
        </div>
        {selected && (
          <p className="text-center text-xs text-zinc-500" data-testid="selected-movie">
            Selected: <span className="text-amber-300">{selected.title} — {selected.year}</span>
          </p>
        )}
      </div>

      {/* guess history */}
      {history.length > 0 && (
        <div className="space-y-2" data-testid="guess-history">
          {history.map((h, i) => (
            <div
              key={i}
              data-testid={`history-item-${i}`}
              className="flex items-center gap-3 px-4 py-3 rounded-lg border border-white/10 bg-black/30"
            >
              <span
                className="w-4 h-4 rounded-sm shrink-0"
                style={{ background: h.result === "correct" ? "#10b981" : "#d92b2b" }}
              />
              <span
                className={
                  h.label === "SKIPPED"
                    ? "text-sm uppercase tracking-wide text-zinc-400"
                    : "text-sm text-zinc-200"
                }
              >
                {h.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl bg-black/40 border border-amber-500/15 py-3 px-2">
    <div className="text-lg sm:text-xl font-bold text-amber-100" data-testid={testid}>{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">{label}</div>
  </div>
);

const CompletedAudioList = ({ date, backendMode }) => (
  <div className="ac-card p-5" data-testid="completed-audio-list">
    <div className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-3 text-center">
      🎧 All Clues · Replay Any Audio
    </div>
    <div className="space-y-3">
      {[1, 2, 3, 4, 5].map((s) => (
        <DialoguePlayer
          key={s}
          mode={backendMode}
          date={date}
          slot={s}
          label={`Audio Clue ${s}`}
          difficulty={DIFFICULTY[s - 1]}
          analyticsContext="completed"
        />
      ))}
    </div>
  </div>
);

const PreviewReset = ({ onReset }) => (
  <button
    onClick={onReset}
    data-testid="preview-reset"
    className="w-full py-3 rounded-xl border border-amber-500/30 text-amber-200 hover:bg-amber-500/10 font-bold"
  >
    ↻ Reset Preview
  </button>
);
